import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { EmptyState, Field, FormGrid, KeyValue, NumberInput, Pill, SelectInput, Spinner, TextInput } from '../../components/ui';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import type { Tone } from '../../lib/format';
import type {
  QcCandidateReason, QcCandidateSummary, QcCheckpoint, QcComparator, QcCriterion,
  QcImportCandidate, QcInspectionKind, QcReviewStatus, QcTemplateDetail, QcTemplateRow,
} from '../../lib/types';

// —— QC 接管第六步：质检模板 / 量化公差人工复核页 ——
// 原则（对齐第三步"不臆测、确认前不自动判定"）：
//   · 双层验收：qi-inspect 描述文本恒保留；ERPNext 量化字段只在人工确认后才可参与自动判定（auto_usable）。
//   · 纯描述项即使确认，auto_usable 仍为 0（描述定稿 ≠ 可自动判合格）。
//   · 候选队列只做「忽略」或「显式指派到已有检查点+参数+有效比较符」，绝不猜绑新键。
//   · 写操作限 quality / admin（后端 requireWritePermission 强制，此处仅体验层显隐）。

const REVIEW_TONE: Record<QcReviewStatus, Tone> = { pending_review: 'warn', confirmed: 'ok', rejected: 'neutral' };
const REVIEW_LABEL: Record<QcReviewStatus, string> = { pending_review: '待复核', confirmed: '已确认', rejected: '已拒绝' };
const KIND_TONE: Record<QcInspectionKind, Tone> = { incoming: 'info', in_process: 'warn', final: 'ok' };
const KIND_LABEL: Record<QcInspectionKind, string> = { incoming: '来料初检', in_process: '工序检验', final: '最终检验' };
const SOURCE_LABEL: Record<string, string> = { 'qi-inspect-text': '移动端文本', 'erpnext-imported': 'ERPNext 导入', manual: '人工指派' };
const REASON_LABEL: Record<QcCandidateReason, string> = {
  template_unmapped: '模板未映射', parameter_unclassified: '参数未分类', no_value: '无公差值',
  no_checkpoint: '无对应检查点', checkpoint_ambiguous: '检查点歧义',
};
const CAND_TONE: Record<string, Tone> = { pending_review: 'warn', assigned: 'ok', dismissed: 'neutral' };
const CAND_LABEL: Record<string, string> = { pending_review: '待处理', assigned: '已指派', dismissed: '已忽略' };
const COMPARATOR_LABEL: Record<QcComparator, string> = { '>=': '≥ 下限', '<=': '≤ 上限', tolerance: '± 公差' };

function unwrap<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as { data: T }).data;
  }
  return body as T;
}
function fmtComparator(c: QcCriterion): string {
  if (!c.comparator) return '';
  if (c.comparator === '>=') return c.target_value == null ? '≥' : `≥ ${c.target_value}${c.unit ? ` ${c.unit}` : ''}`;
  if (c.comparator === '<=') return c.max_value == null ? '≤' : `≤ ${c.max_value}${c.unit ? ` ${c.unit}` : ''}`;
  return c.tolerance ? `± ${c.tolerance}${c.unit ? ` ${c.unit}` : ''}` : '± 公差';
}

// 单条验收标准行：量化字段 + 双层文本 + 复核动作（确认/拒绝）。
function CriterionRow({ c, editable, onReviewed }: { c: QcCriterion; editable: boolean; onReviewed: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const quantified = !!c.comparator;

  const review = async (decision: 'confirm' | 'reject') => {
    setErr(null); setBusy(decision);
    try {
      await api.post(`/api/qc-templates/criteria/${c.id}/review`, { decision });
      onReviewed();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '复核失败');
    } finally { setBusy(null); }
  };

  return (
    <tr>
      <td>
        <div style={{ fontWeight: 600 }}>{c.parameter_label || c.parameter_key || '（未命名参数）'}</div>
        <div className="muted" style={{ fontSize: 12 }}>{c.criterion_text || '—'}</div>
        {err ? <div className="field-hint bad">{err}</div> : null}
      </td>
      <td>
        {quantified
          ? <span className="num">{fmtComparator(c)}</span>
          : <span className="muted">仅文本（无量化）</span>}
      </td>
      <td><span className="muted" style={{ fontSize: 12 }}>{SOURCE_LABEL[c.source] || c.source}</span></td>
      <td>
        <Pill tone={REVIEW_TONE[c.review_status]} label={REVIEW_LABEL[c.review_status]} />
        {c.auto_usable ? <Pill tone="info" label="可自动判定" /> : null}
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {editable ? (
          c.review_status === 'pending_review' ? (
            <>
              <button className="btn primary" onClick={() => review('confirm')} disabled={!!busy}>{busy === 'confirm' ? '…' : '确认'}</button>{' '}
              <button className="btn" onClick={() => review('reject')} disabled={!!busy}>{busy === 'reject' ? '…' : '拒绝'}</button>
            </>
          ) : <span className="muted" style={{ fontSize: 12 }}>已复核 · 如需改判请重新导入</span>
        ) : <span className="muted" style={{ fontSize: 12 }}>只读</span>}
      </td>
    </tr>
  );
}

// 单个检查点：参数 + 其下的双层验收标准。
function CheckpointBlock({ cp, editable, onReviewed }: { cp: QcCheckpoint; editable: boolean; onReviewed: () => void }) {
  const pending = cp.criteria.filter((x) => x.review_status === 'pending_review' && x.comparator).length;
  return (
    <div className="panel" style={{ marginBottom: 10 }}>
      <div className="panel-head">
        <div>
          <h3 style={{ fontSize: 14, margin: 0 }}>{cp.name_zh}</h3>
          <div className="desc">{cp.parameters.map((p) => p.parameter_label || p.parameter_key).join('、') || '无采集参数'}</div>
        </div>
        <div className="spacer" />
        {cp.inspection_kind ? <Pill tone={KIND_TONE[cp.inspection_kind]} label={KIND_LABEL[cp.inspection_kind]} /> : null}
        {pending > 0 ? <Pill tone="warn" label={`${pending} 项量化待复核`} /> : null}
      </div>
      <div className="panel-body" style={{ padding: 0 }}>
        <table className="lines">
          <thead>
            <tr><th>参数 / 描述</th><th>量化公差</th><th>来源</th><th>复核状态</th><th style={{ textAlign: 'right' }}>操作</th></tr>
          </thead>
          <tbody>
            {cp.criteria.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 10 }}><span className="muted" style={{ fontSize: 12.5 }}>该检查点暂无验收标准</span></td></tr>
            ) : cp.criteria.map((c) => <CriterionRow key={c.id} c={c} editable={editable} onReviewed={onReviewed} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// 模板明细：阶段 → 检查点 → 双层验收标准（可复核）。
function TemplateReview({ row, onChanged }: { row: QcTemplateRow; onChanged: () => void }) {
  const { user } = useAuth();
  const editable = can(user, 'qc-templates', 'edit');
  const { data, loading, error, reload } = useDetail<QcTemplateDetail>(`/api/qc-templates/${row.id}`);
  const refresh = () => { reload(); onChanged(); };

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{row.code}</span>
        <h3>{row.name_zh}</h3>
        <div className="spacer" />
        {loading ? <Spinner label="加载检查点…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      {!editable ? <div className="notice" style={{ marginBottom: 10 }}>当前角色只读复核，确认/拒绝由质量角色执行。</div> : null}
      {data?.stages?.length ? data.stages.map((s) => (
        <div key={s.id} style={{ marginBottom: 6 }}>
          <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '8px 0 6px' }}>
            {s.stage_order}. {s.stage_name}<span className="muted"> · {s.stage_key}</span>
          </div>
          {s.checkpoints.map((cp) => <CheckpointBlock key={cp.id} cp={cp} editable={editable} onReviewed={refresh} />)}
        </div>
      )) : (!loading && !error ? <EmptyState>该模板暂无阶段 / 检查点。</EmptyState> : null)}
    </div>
  );
}

// 候选指派表单：显式选定检查点 + 参数键 + 有效比较符，落 pending_review / auto_usable=0 的 manual 标准。
function CandidateAssignForm({ cand, onDone, onCancel }: { cand: QcImportCandidate; onDone: () => void; onCancel: () => void }) {
  const { data } = useDetail<QcTemplateDetail>(cand.target_template_code ? `/api/qc-templates/${cand.target_template_code}` : null);
  const checkpoints = useMemo(() => (data?.stages ?? []).flatMap((s) => s.checkpoints.map((c) => ({
    ...c, stage_name: s.stage_name,
  }))), [data]);

  const [checkpointId, setCheckpointId] = useState('');
  const [paramKey, setParamKey] = useState(cand.parameter_key || '');
  const [comparator, setComparator] = useState<QcComparator>((cand.comparator as QcComparator) || '>=');
  const [targetValue, setTargetValue] = useState(cand.target_value != null ? String(cand.target_value) : '');
  const [maxValue, setMaxValue] = useState(cand.max_value != null ? String(cand.max_value) : '');
  const [tolerance, setTolerance] = useState(cand.tolerance || '');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!checkpointId) { setErr('请选择要绑定的质检检查点'); return; }
    if (!paramKey.trim()) { setErr('请填写参数键（不可臆测新键，请选目录内已有键）'); return; }
    setErr(null); setBusy(true);
    try {
      const body: Record<string, unknown> = { decision: 'assign', checkpoint_id: checkpointId, parameter_key: paramKey.trim(), comparator, notes: notes || undefined };
      if (comparator === '>=' && targetValue !== '') body.target_value = Number(targetValue);
      if (comparator === '<=' && maxValue !== '') body.max_value = Number(maxValue);
      if (comparator === 'tolerance' && tolerance !== '') body.tolerance = tolerance;
      await api.post(`/api/qc-templates/import-candidates/${cand.id}/review`, body);
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '指派失败');
    } finally { setBusy(false); }
  };

  return (
    <div className="panel" style={{ marginTop: 8, padding: 12, background: 'var(--surface-2, rgba(0,0,0,.02))' }}>
      <div style={{ fontSize: 12.5, marginBottom: 8 }}>
        指派「{cand.source_template_name} · {cand.specification}」到 {cand.target_template_code || '目标模板'} 的既有检查点。指派结果仍标为待复核，确认前不可自动判定。
      </div>
      {err ? <div className="notice bad" style={{ marginBottom: 8 }}>{err}</div> : null}
      <FormGrid min={180}>
        <Field label="绑定检查点" required>
          <SelectInput value={checkpointId} onChange={(e) => setCheckpointId(e.target.value)}>
            <option value="">{checkpoints.length ? '请选择…' : '（先确保目标模板已导入检查点）'}</option>
            {checkpoints.map((c) => <option key={c.id} value={c.id}>{c.stage_name} · {c.name_zh}</option>)}
          </SelectInput>
        </Field>
        <Field label="参数键" required hint="须为参数目录内已有键">
          <TextInput value={paramKey} onChange={(e) => setParamKey(e.target.value)} placeholder="如 diameter / hardness" />
        </Field>
        <Field label="比较符">
          <SelectInput value={comparator} onChange={(e) => setComparator(e.target.value as QcComparator)}>
            {(['>=', '<=', 'tolerance'] as QcComparator[]).map((c) => <option key={c} value={c}>{COMPARATOR_LABEL[c]}</option>)}
          </SelectInput>
        </Field>
        {comparator === '>=' ? (
          <Field label="下限值"><NumberInput value={targetValue} onChange={(e) => setTargetValue(e.target.value)} /></Field>
        ) : comparator === '<=' ? (
          <Field label="上限值"><NumberInput value={maxValue} onChange={(e) => setMaxValue(e.target.value)} /></Field>
        ) : (
          <Field label="公差 ±"><TextInput value={tolerance} onChange={(e) => setTolerance(e.target.value)} placeholder="如 0.05" /></Field>
        )}
        <Field label="备注" span><TextInput value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="判定依据（可选）" /></Field>
      </FormGrid>
      <div className="detail-actions" style={{ marginTop: 8 }}>
        <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '确认指派'}</button>
        <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
      </div>
    </div>
  );
}

function CandidatesPanel({ onChanged }: { onChanged: () => void }) {
  const { user } = useAuth();
  const editable = can(user, 'qc-templates', 'edit');
  const [rows, setRows] = useState<QcImportCandidate[]>([]);
  const [summary, setSummary] = useState<QcCandidateSummary | null>(null);
  const [reason, setReason] = useState<'' | QcCandidateReason>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignId, setAssignId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const body = await api.get<{ data: QcImportCandidate[]; summary: QcCandidateSummary }>(
        `/api/qc-templates/import-candidates${reason ? `?reason=${reason}` : ''}`);
      setRows(unwrap<QcImportCandidate[]>(body));
      setSummary((body as { summary?: QcCandidateSummary }).summary ?? null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '候选加载失败');
    } finally { setLoading(false); }
  }, [reason]);

  useEffect(() => { void load(); }, [load]);

  const dismiss = async (c: QcImportCandidate) => {
    setError(null);
    try {
      await api.post(`/api/qc-templates/import-candidates/${c.id}/review`, { decision: 'dismiss' });
      await load(); onChanged();
    } catch (e) { setError(e instanceof ApiError ? e.message : '忽略失败'); }
  };

  const visible = showAll ? rows : rows.filter((r) => r.review_status === 'pending_review');

  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>ERPNext 导入候选队列</h2>
          <div className="desc">无法无歧义自动绑定的项：只能「忽略」或「显式指派到已有检查点+参数」。指派后仍待复核、不自动判定。</div>
        </div>
        <div className="spacer" />
        <div className="field">
          <label>异常原因</label>
          <SelectInput value={reason} onChange={(e) => setReason(e.target.value as '' | QcCandidateReason)}>
            <option value="">全部</option>
            {(Object.keys(REASON_LABEL) as QcCandidateReason[]).map((r) => (
              <option key={r} value={r}>{REASON_LABEL[r]}{summary?.by_reason[r] ? ` (${summary.by_reason[r]})` : ''}</option>
            ))}
          </SelectInput>
        </div>
      </div>
      {summary ? (
        <div className="kv-grid" style={{ padding: '10px var(--pad) 0' }}>
          <KeyValue k="候选总数">{summary.total}</KeyValue>
          <KeyValue k="待处理">{summary.by_review_status.pending_review ?? 0}</KeyValue>
          <KeyValue k="已指派">{summary.by_review_status.assigned ?? 0}</KeyValue>
          <KeyValue k="已忽略">{summary.by_review_status.dismissed ?? 0}</KeyValue>
          <KeyValue k="已量化绑定">{summary.quantified_bound}</KeyValue>
        </div>
      ) : null}
      <div className="toolbar" style={{ padding: '8px var(--pad) 0' }}>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5 }}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />显示已处理项
        </label>
        <div className="spacer" />
        <button className="btn" onClick={load} disabled={loading}>刷新</button>
      </div>
      {error ? <div className="notice bad" style={{ margin: '0 var(--pad)' }}>{error}</div> : null}
      <div className="table-wrap">
        {loading ? (
          <div style={{ padding: 'var(--pad)' }}><Spinner label="正在读取候选…" /></div>
        ) : visible.length === 0 ? (
          <EmptyState>{showAll ? '暂无候选。' : '没有待处理候选——异常项都已指派或忽略。'}</EmptyState>
        ) : (
          <table className="grid">
            <thead>
              <tr><th>ERPNext 模板 / 规格</th><th>原文 / 值</th><th>原因</th><th>状态</th><th style={{ textAlign: 'right' }}>操作</th></tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{c.source_template_name}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{c.target_template_code ? `→ ${c.target_template_code}` : '（模板未映射）'}{c.stage_hint ? ` · ${c.stage_hint}` : ''}</div>
                  </td>
                  <td>
                    <div>{c.specification}</div>
                    <div className="muted num" style={{ fontSize: 12 }}>{c.raw_value ? `原值 ${c.raw_value}` : '无值'}{c.parameter_key ? ` · ${c.parameter_key}` : ''}</div>
                  </td>
                  <td><Pill tone={c.reason === 'checkpoint_ambiguous' ? 'warn' : 'neutral'} label={REASON_LABEL[c.reason]} /></td>
                  <td><Pill tone={CAND_TONE[c.review_status] || 'neutral'} label={CAND_LABEL[c.review_status] || c.review_status} /></td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {!editable ? <span className="muted" style={{ fontSize: 12 }}>只读</span> : c.review_status !== 'pending_review' ? (
                      <span className="muted" style={{ fontSize: 12 }}>已处理{c.notes ? ` · ${c.notes}` : ''}</span>
                    ) : (
                      <>
                        <button className="btn" onClick={() => dismiss(c)}>忽略</button>{' '}
                        {c.target_template_code
                          ? <button className="btn primary" onClick={() => setAssignId(assignId === c.id ? null : c.id)}>{assignId === c.id ? '收起' : '指派'}</button>
                          : <span className="muted" style={{ fontSize: 12 }} title="模板未映射，需先在来源侧建立映射，不在此猜绑">需先映射</span>}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {assignId ? (() => {
          const c = visible.find((x) => x.id === assignId);
          return c ? (
            <tr className="detail-row"><td colSpan={5}>
              <CandidateAssignForm cand={c} onCancel={() => setAssignId(null)} onDone={async () => { setAssignId(null); await load(); onChanged(); }} />
            </td></tr>
          ) : null;
        })() : null}
      </div>
    </section>
  );
}

export function QcTemplatesPage() {
  const { user } = useAuth();
  const editable = can(user, 'qc-templates', 'edit');
  const [tab, setTab] = useState<'criteria' | 'candidates'>('criteria');
  const [reloadSignal, setReloadSignal] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const columns: Column<QcTemplateRow>[] = [
    { header: '编码', render: (t) => <span className="num strong">{t.code}</span> },
    { header: '模板名称', render: (t) => t.name_zh },
    { header: '阶段/检查点', align: 'right', render: (t) => <span className="muted">{t.stage_count ?? 0} / {t.checkpoint_count ?? 0}</span> },
    { header: '量化可判定', align: 'right', render: (t) => (t.quantified_count ? <Pill tone="info" label={`${t.quantified_count}`} /> : <span className="muted">0</span>) },
    { header: '待复核', align: 'right', render: (t) => (t.pending_review_count ? <Pill tone="warn" label={`${t.pending_review_count}`} /> : <span className="muted">0</span>) },
  ];

  const run = async (kind: 'import' | 'tolerances' | 'generate') => {
    setBusy(kind); setMsg(null);
    const path = kind === 'import' ? '/api/qc-templates/import' : kind === 'tolerances' ? '/api/qc-templates/import-tolerances' : '/api/qc-templates/generate-process-templates';
    try {
      const body = await api.post<{ data: Record<string, unknown> }>(path, {});
      const d = unwrap<Record<string, unknown>>(body);
      const label = kind === 'import' ? '模板导入' : kind === 'tolerances' ? '量化公差导入' : '草稿工艺模板生成';
      const hint = kind === 'import' ? `模板 ${d.templates} · 检查点 ${d.checkpoints}` : kind === 'tolerances' ? `绑定 ${d.bound} · 候选 ${d.candidates}` : `新建 ${d.created} · 更新 ${d.updated} · 跳过 ${d.skipped}`;
      setMsg(`${label}完成：${hint}`);
      setReloadSignal((n) => n + 1);
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : '操作失败');
    } finally { setBusy(null); }
  };

  return (
    <>
      <div className="toolbar" style={{ alignItems: 'center', marginBottom: 4 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className={`btn ${tab === 'criteria' ? 'primary' : ''}`} onClick={() => setTab('criteria')}>量化公差复核</button>
          <button className={`btn ${tab === 'candidates' ? 'primary' : ''}`} onClick={() => setTab('candidates')}>导入候选队列</button>
        </div>
        <div className="spacer" />
        {editable ? (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn" onClick={() => run('import')} disabled={!!busy}>{busy === 'import' ? '…' : '重导入模板'}</button>
            <button className="btn" onClick={() => run('tolerances')} disabled={!!busy}>{busy === 'tolerances' ? '…' : '导入量化公差'}</button>
            <button className="btn" onClick={() => run('generate')} disabled={!!busy}>{busy === 'generate' ? '…' : '生成草稿工艺模板'}</button>
          </div>
        ) : null}
      </div>
      {msg ? <div className="notice" style={{ margin: '4px 0 10px' }}>{msg}</div> : null}

      {tab === 'criteria' ? (
        <ListPage<QcTemplateRow>
          title="质检模板 · 双层验收复核"
          desc="qi-inspect 描述文本 + ERPNext 量化公差并存。量化项确认前不自动判合格；确认后（带比较符）方可用于自动判定。展开模板逐检查点复核。"
          endpoint="/api/qc-templates"
          columns={columns}
          rowId={(t) => t.id}
          showYear={false}
          searchPlaceholder="搜索模板编码 / 名称…"
          reloadSignal={reloadSignal}
          renderDetail={(row) => <TemplateReview row={row} onChanged={() => setReloadSignal((n) => n + 1)} />}
        />
      ) : (
        <CandidatesPanel onChanged={() => setReloadSignal((n) => n + 1)} />
      )}
    </>
  );
}
